import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { LuX, LuChevronDown, LuPlus } from 'react-icons/lu';
import type { CustomFieldType } from '../types';

interface AddCustomFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: {
    name: string;
    field_type: CustomFieldType;
    is_required: boolean;
    unit_name?: string | null;
    options?: string[];
  }) => void;
  isLoading?: boolean;
}

const FIELD_TYPE_OPTIONS: { value: CustomFieldType; label: string }[] = [
  { value: 'short_text', label: '1行テキスト' },
  { value: 'long_text', label: '複数行テキスト' },
  { value: 'single_select', label: '単一選択' },
  { value: 'multiple_select', label: '複数選択' },
  { value: 'date', label: '日付' },
  { value: 'datetime', label: '日時' },
  { value: 'number', label: '数値' },
];

/**
 * カスタムフィールド追加モーダル
 * field_typeに応じて表示するフィールドを変更
 */
export default function AddCustomFieldModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}: AddCustomFieldModalProps) {
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState<CustomFieldType>('short_text');
  const [isRequired, setIsRequired] = useState(false);
  const [unitName, setUnitName] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // モーダルが閉じたらフォームをリセット
  useEffect(() => {
    if (!isOpen) {
      setFieldName('');
      setFieldType('short_text');
      setIsRequired(false);
      setUnitName('');
      setOptions([]);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    // バリデーション
    if (!fieldName.trim()) {
      setError('項目名を入力してください');
      return;
    }

    // 選択肢タイプの場合、選択肢が必要
    if (
      (fieldType === 'single_select' || fieldType === 'multiple_select') &&
      options.length === 0
    ) {
      setError('選択肢を1つ以上入力してください');
      return;
    }

    setError(null);
    onSubmit({
      name: fieldName,
      field_type: fieldType,
      is_required: isRequired,
      unit_name: fieldType === 'number' ? unitName || null : null,
      options:
        fieldType === 'single_select' || fieldType === 'multiple_select'
          ? options.filter((opt) => opt.trim())
          : undefined,
    });
  };

  const addOption = () => {
    setOptions([...options, '']);
  };

  const removeOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  // フィールドタイプに応じた表示判定
  const showUnitName = fieldType === 'number';
  const showOptions = fieldType === 'single_select' || fieldType === 'multiple_select';

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="w-[640px]">
        <AlertDialogHeader>
          <AlertDialogTitle>項目を追加</AlertDialogTitle>
        </AlertDialogHeader>

        {/* スクロール可能なコンテンツ */}
        <div className="max-h-[520px] space-y-6 overflow-x-hidden overflow-y-auto px-6 py-4">
          {/* 項目名入力フィールド */}
          <div className="flex w-[236px] flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-900">項目名</label>
              <span className="h-5 rounded-full bg-red-50 px-3 text-xs leading-5 font-bold text-red-600">
                必須
              </span>
            </div>
            <Input
              type="text"
              value={fieldName}
              onChange={(e) => {
                setFieldName(e.target.value);
                setError(null);
              }}
              placeholder="例：コメント"
              disabled={isLoading}
            />
          </div>

          {/* 情報タイプ選択 */}
          <div className="flex w-[236px] flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-900">情報タイプ</label>
              <span className="h-5 rounded-full bg-red-50 px-3 text-xs leading-5 font-bold text-red-600">
                必須
              </span>
            </div>
            <div className="relative">
              <select
                value={fieldType}
                onChange={(e) => {
                  setFieldType(e.target.value as CustomFieldType);
                  setError(null);
                }}
                disabled={isLoading}
                className="h-10 w-full appearance-none rounded border border-solid border-gray-300 bg-white px-3 pr-8 text-sm text-gray-900 focus:border-blue-600 focus:outline-hidden disabled:bg-gray-50"
              >
                {FIELD_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <LuChevronDown
                size={16}
                className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-gray-600"
              />
            </div>
          </div>

          {/* 単位名入力フィールド（数値タイプの場合のみ） */}
          {showUnitName && (
            <div className="flex w-[236px] flex-col gap-2">
              <label className="text-sm font-bold text-gray-900">単位名</label>
              <Input
                type="text"
                value={unitName}
                onChange={(e) => setUnitName(e.target.value)}
                placeholder="例：円"
                disabled={isLoading}
              />
            </div>
          )}

          {/* 選択肢入力フィールド（単一選択・複数選択の場合のみ） */}
          {showOptions && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-bold text-gray-900">選択肢</label>
              <div className="space-y-2">
                {options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`選択肢${index + 1}`}
                      disabled={isLoading}
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="rounded p-1 hover:bg-gray-100"
                      disabled={isLoading}
                    >
                      <LuX size={16} className="text-gray-600" />
                    </button>
                  </div>
                ))}
                {options.length === 0 && (
                  <p className="text-sm text-gray-500">選択肢を追加してください</p>
                )}
              </div>
              <Button
                color="secondary"
                size="sm"
                onClick={addOption}
                disabled={isLoading}
                className="w-[108px]"
              >
                <LuPlus size={16} />
                選択肢を追加
              </Button>
            </div>
          )}

          {/* 必須入力トグル */}
          <div className="flex w-[236px] flex-col gap-2">
            <label className="text-sm font-bold text-gray-900">必須入力にする</label>
            <div className="flex justify-start">
              <button
                type="button"
                onClick={() => setIsRequired(!isRequired)}
                className={`relative h-5 w-10 rounded-full transition-colors ${
                  isRequired ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                disabled={isLoading}
              >
                <span
                  className={`absolute top-[2px] size-4 rounded-full bg-white transition-transform ${
                    isRequired ? 'left-[22px]' : 'left-[2px]'
                  }`}
                />
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <AlertDialogFooter>
          <Button color="secondary" size="md" onClick={onClose} disabled={isLoading}>
            キャンセル
          </Button>
          <Button
            color="primary"
            size="md"
            onClick={handleSubmit}
            disabled={isLoading || !fieldName.trim()}
          >
            {isLoading ? '追加中...' : '決定'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
